import {
  FGTextInput,
  FieldGroup,
  FGSelectInput,
  FGCustomPanel,
} from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import * as Yup from "yup";

import {
  HealthPackApi,
  HealthPackDto,
  HealthPackTranslationDto,
} from "../../../../api";
import { ERoutes } from "../../../../ERoutes";
import { FGMultiTranslate, SmallFormGenerator } from "../../../../components";
import { useGlobalData } from "../../../../contexts";
import { useApiService, useCrudApi, useTl, useWindow } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { useReferential } from "../../../../hooks/useReferential";
import { CharacteristicSelector } from "./CharacteristicSelector";

export interface IHealthPackDetailPageProps {
  healthPackId: number;
  editMode: boolean;
}

export const HealthPackDetailPage: React.FunctionComponent<IHealthPackDetailPageProps> = ({
  healthPackId,
  editMode,
}) => {
  const history = useHistory();
  const { t } = useTl();
  const api = useApiService(HealthPackApi);
  const { languages } = useGlobalData();
  const { openNewTab } = useWindow();
  const [groups, groupsLoading] = useReferential((c) =>
    c.getHealthPackGroups()
  );
  const [articles, articlesLoading] = useReferential((c) =>
    c.getArticles(true)
  );
  const [labs, lLoading] = useReferential((a) => a.getLabs(), false, []);

  const { data, loading, saveItem, saving, deleteItem, deleting } = useCrudApi(
    {
      getApiFn: (id = healthPackId) =>
        id > 0
          ? api.getHealthPack(id)
          : new HealthPackDto({
              characteristicIds: [],
              healthPackTranslations: [
                ...languages.map(
                  (l) =>
                    new HealthPackTranslationDto({
                      languageId: l.languageId,
                    })
                ),
              ],
            }).toJSON(),
      saveApiFn: (d: HealthPackDto) => api.save(d),
      onSavedRoute: (d) =>
        `${ERoutes.healthPack}/${d.healthPackId}/detail/edit`,
      deleteApiFn: (d) => api.delete(d.healthPackId),
      onDeletedRoute: (d) => `${ERoutes.healthPack}`,
    },
    [healthPackId]
  );

  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      lab: Yup.string().nullable().required(t(ETLCodes.Required)),
      healthPackCode: Yup.string().nullable().required(t(ETLCodes.Required)),
      articleId: Yup.number().nullable().required(t(ETLCodes.Required)),
      healthPackGroupId: Yup.number().nullable().required(t(ETLCodes.Required)),
      healthPackTranslations: Yup.array().of(
        Yup.object().shape({
          text: Yup.string().nullable().required(t(ETLCodes.Required)),
        })
      ),
    });
  }, [t]);

  return (
    data && (
      <SmallFormGenerator
        initialValues={data}
        onSubmit={saveItem}
        editMode={editMode}
        loading={loading}
        onCancel={() => history.push(ERoutes.healthPack)}
        saving={saving}
        deleting={deleting}
        showDeleteButton={healthPackId > 0}
        onDelete={deleteItem}
        validationSchema={FormSchema}
      >
        <FieldGroup>
          <FGSelectInput
            name="lab"
            label={t(ETLCodes.Laboratory)}
            items={labs}
            loading={lLoading}
            disabled={healthPackId > 0}
          />
          <FieldGroup columns={2}>
            <FGTextInput label={t(ETLCodes.LabCode)} name="healthPackCode" />
          </FieldGroup>
          <FGSelectInput
            label={t(ETLCodes.HealthPackGroup)}
            name="healthPackGroupId"
            loading={groupsLoading}
            items={groups}
          />
          <FGMultiTranslate
            label={ETLCodes.Name}
            translationsArrayField="healthPackTranslations"
            translationField="text"
            translationDto={HealthPackTranslationDto}
          />
          <FGSelectInput
            items={articles}
            loading={articlesLoading}
            name="articleId"
            label={t(ETLCodes.Article)}
            onItemViewClick={(value) =>
              openNewTab(`${ERoutes.article}/${value}/detail/view`)
            }
          />
          <FGTextInput
            label={t(ETLCodes.WebsiteAdresseFr)}
            name="webSiteUriFr"
          />
          <FGTextInput
            label={t(ETLCodes.WebsiteAdresseNl)}
            name="webSiteUriNl"
          />
          <FGCustomPanel>
            {({ formik }) => (
              <CharacteristicSelector
                linkedIds={formik?.values?.characteristicIds || []}
                onLinkedIdsChanged={(ids) =>
                  formik?.setFieldValue("characteristicIds", ids)
                }
                editMode={editMode}
                loading={loading}
                lab={formik?.values?.lab}
                onlyIsLab={true}
              />
            )}
          </FGCustomPanel>
        </FieldGroup>
      </SmallFormGenerator>
    )
  );
};
